import { type MutableRefObject, useCallback, useEffect, useRef } from "react";

type UseWebSocketOptions = {
  url: string;
  onOpen?: (ws: WebSocket) => void | Promise<void>;
  onMessage?: (event: MessageEvent) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
};

export function useWebSocket({
  url,
  onOpen,
  onMessage,
  onClose,
  onError,
}: UseWebSocketOptions): {
  socket: MutableRefObject<WebSocket | null>;
  connect: () => WebSocket;
  disconnect: (code?: number, reason?: string) => void;
  readyState: number;
} {
  const socketRef = useRef<WebSocket | null>(null);

  const connect = useCallback(() => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      return socketRef.current;
    }
    const ws = new WebSocket(url);
    socketRef.current = ws;
    ws.onopen = (): void => {
      void onOpen?.(ws);
    };
    ws.onmessage = (event): void => onMessage?.(event);
    ws.onclose = (event): void => onClose?.(event);
    ws.onerror = (event): void => onError?.(event);

    return ws;
  }, [url, onOpen, onMessage, onClose, onError]);

  const disconnect = useCallback((code = 1000, reason = "Closed by user") => {
    const ws = socketRef.current;
    if (!ws) return;
    if (
      ws.readyState === WebSocket.OPEN ||
      ws.readyState === WebSocket.CONNECTING
    ) {
      ws.close(code, reason);
    }
    socketRef.current = null;
  }, []);

  useEffect(() => {
    return (): void => {
      disconnect();
    };
  }, [disconnect]);

  return {
    socket: socketRef,
    connect,
    disconnect,
    readyState: socketRef.current?.readyState ?? WebSocket.CLOSED,
  };
}
